/**
 * Amenity Service
 * Maps amenity names from API to icon components
 */

import {
  BalconyIcon,
  BedIcon,
  BikeIcon,
  BuildingIcon,
  CctvIcon,
  ChildrenPlayAreaIcon,
  CircleParkingIcon,
  CoveredParkingIcon,
  DumbbellIcon,
  GymIcon,
  IconMosqueIcon,
  IconTrackIcon,
  JoggingTrackIcon,
  PetsAllowedIcon,
  RestaurantIcon,
  ShowerHeadIcon,
  StoreIcon,
  SwimmingPoolIcon,
  VolleyballIcon,
  WardrobesIcon,
  WavesIcon,
  WavesLadderIcon,
} from "@/components/atoms/icons";
import type { ComponentType } from "react";

/**
 * Icon component mapping
 * Includes all icons that are already imported in AboutProperty.tsx
 */
export const AMENITY_ICON_COMPONENTS: Record<string, ComponentType<any>> = {
  BedIcon,
  BikeIcon,
  BuildingIcon,
  CctvIcon,
  CircleParkingIcon,
  DumbbellIcon,
  IconMosqueIcon,
  IconTrackIcon,
  ShowerHeadIcon,
  StoreIcon,
  VolleyballIcon,
  WavesIcon,
  WavesLadderIcon,
  BalconyIcon,
  ChildrenPlayAreaIcon,
  CoveredParkingIcon,
  GymIcon,
  JoggingTrackIcon,
  PetsAllowedIcon,
  RestaurantIcon,
  SwimmingPoolIcon,
  WardrobesIcon,
};

/**
 * Amenity name to icon component name mapping
 * Maps API amenity names to icon component names.
 * Existing icons are used where they fit; new ones added for 8+ amenity types.
 */
const AMENITY_MAPPINGS: Record<string, string> = {
  // Gym & fitness
  Gym: "GymIcon",
  "State of The Art Gymnasium": "DumbbellIcon",

  // Pool & water
  "Swimming Pool": "SwimmingPoolIcon",
  "Swimming Pools": "WavesLadderIcon",
  "Central Lake": "WavesIcon",

  // Parking
  "Covered Parking": "CoveredParkingIcon",
  "Parking Space": "CircleParkingIcon",

  // Tracks & outdoor (include variants API might send)
  "Jogging Track": "JoggingTrackIcon",
  JoggingTrack: "JoggingTrackIcon",
  "Running Track": "JoggingTrackIcon",
  "Walking & Running Tracks": "JoggingTrackIcon",
  "Cycling Tracks": "BikeIcon",

  // F&B & retail
  Restaurant: "RestaurantIcon",
  "Community Center - F&B, Retail Outlets": "StoreIcon",
  "Retail Outlets": "RestaurantIcon",
  Cinema: "RestaurantIcon",
  "Movie Theater": "RestaurantIcon",
  "Screening Room": "StoreIcon",

  // Play & family
  "Children Play Area": "ChildrenPlayAreaIcon",
  "Kids Play Areas": "VolleyballIcon",

  // Security
  "24-7 Security Services": "CctvIcon",
  Security: "CctvIcon",
  "Security Services": "ShieldIcon",

  // Worship
  Mosques: "IconMosqueIcon",

  // Garden & green
  Garden: "SproutIcon",
  Parks: "SproutIcon",
  "Landscaped Gardens": "SproutIcon",
  "Green Area": "SproutIcon",

  // Unit features (replaced BuildingIcon with specific icons)
  Balcony: "BalconyIcon",
  "Built-in Wardrobes": "WardrobesIcon",
  "Pets Allowed": "PetsAllowedIcon",
  "Pet Friendly": "ShieldIcon",

  // Premium & wellness
  Concierge: "CrownIcon",
  "Concierge Service": "CrownIcon",
  Spa: "ShowerHeadIcon",
  "Wellness Center": "ShowerHeadIcon",
};

/**
 * Normalize for matching: trim, any whitespace (including \u00A0) to space, _/- to space,
 * collapse spaces, lowercase. Ensures "Jogging\u00A0Track" and "jogging_track" match.
 */
export function normalizeAmenityName(name: string): string {
  return name
    .trim()
    .replace(/\s/g, " ")
    .replace(/[\s_-]+/g, " ")
    .toLowerCase();
}

/**
 * Same as `normalizeAmenities`, for payloads that may arrive as a bare string
 * instead of an array. Entries are kept whole — commas belong to the label.
 */
export function normalizeAmenitiesList(
  amenities: readonly string[] | string | null | undefined,
): string[] {
  if (amenities == null) {
    return [];
  }

  return normalizeAmenities(
    typeof amenities === 'string' ? [amenities] : amenities,
  );
}

/**
 * Resolve amenity name to mapping key.
 * Handles exact match, then normalized match (Unicode spaces, case, underscores, hyphens).
 */
function resolveAmenityKey(name: string): string | undefined {
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  if (AMENITY_MAPPINGS[trimmed]) return trimmed;
  const normalized = normalizeAmenityName(name);
  if (!normalized) return undefined;
  return Object.keys(AMENITY_MAPPINGS).find(
    (k) => normalizeAmenityName(k) === normalized,
  );
}

/**
 * Get amenity icon component for a given amenity name.
 * Lookup: exact -> normalized (trim, case, whitespace, _/-) -> BuildingIcon.
 */
export function getAmenityIcon(amenityName: string): ComponentType<any> {
  const key = resolveAmenityKey(amenityName);
  if (!key) return BuildingIcon;
  const iconName = AMENITY_MAPPINGS[key];
  if (!iconName) return BuildingIcon;
  const IconComponent = AMENITY_ICON_COMPONENTS[iconName];
  if (IconComponent) return IconComponent;
  return BuildingIcon;
}

/**
 * API sends one amenity per array entry, so entries are never split: a label
 * that contains a comma ("Gym, Indoor") stays a single amenity. Trims and
 * de-dupes only.
 */
export function normalizeAmenities(
  amenities: readonly string[] | null | undefined,
): string[] {
  if (!amenities?.length) {
    return [];
  }
  const seen = new Set<string>();
  const titles: string[] = [];
  for (const entry of amenities) {
    if (typeof entry !== "string") {
      continue;
    }
    const title = entry.trim();
    if (!title) {
      continue;
    }
    const dedupeKey = normalizeAmenityName(title);
    if (!dedupeKey || seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    titles.push(title);
  }
  return titles;
}

export interface AmenityWithIcon {
  title: string;
  Icon: ComponentType<any>;
}

/** Pair each amenity title with its mapped icon for UI grids. */
export function getAmenitiesWithIcons(
  amenities: readonly string[] | null | undefined,
): AmenityWithIcon[] {
  return normalizeAmenities(amenities).map((title) => ({
    title,
    Icon: getAmenityIcon(title),
  }));
}
